import React from 'react';
import Table, { TableColumn } from '../../components/common/Table';
import { RplDTO } from '../../services/apiClient';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import { getBadgeColors, renderBooleanBadge } from '../../utils/displayUtils';
import { getRoles } from '../../utils/tokenUtils';
import Badge from '../common/Badge';
import FileIcons from '../common/FileIcons';
import { Mail } from 'lucide-react';
import createActivityActions from '../actionMenus/RplActions';
import FeedbackBubble from '../common/FeedbackBubble';
import { useRplApi } from '../../hooks/useRplApi';
import MemberNoteIcons from '../common/MemberNoteIcons';
import ReviewPanel from '../reviewPanels/ReviewPanel';

interface RplTableProps {
  data: RplDTO[];
  handleViewMemberDetails: (row: RplDTO) => void;
  handleView: (row: RplDTO) => void;
  handleEdit: (row: RplDTO) => void;
  handleDelete: (row: RplDTO) => Promise<void>;
  handleSendEmail: (row: RplDTO) => void;
  handleViewAllEmails: (row: RplDTO) => void;
  handleViewRecordEmails: (row: RplDTO) => void;
  handleRestore: (row: RplDTO) => Promise<void>;
  handleApprove: (row: RplDTO) => Promise<void>;
  handleChangeStatus: (row: RplDTO) => void;
  handleImpersonateMemberLogin: (row: RplDTO) => void;
  handleAddNote: (row: RplDTO) => void;
  handleViewNoteHistory: (row: RplDTO) => void;
  handleViewInvoiceHistory: (row: RplDTO) => void;
  handleSendProofOfPaymentLink: (row: RplDTO) => void;
  handleCompleteEftPayment: (row: RplDTO) => void;
  handleDownloadForfeitureDocument?: (row: RplDTO) => void;
  handleDownloadOriginalCertificate?: (row: RplDTO) => void;
  handleMemberClick?: (memberId: string) => void;
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  isLoading: boolean;
  sortBy?: string;
  sortDescending?: boolean;
  onSort?: (sortKey: string) => void;
  selectedIds?: Set<string>;
  onSelectRow?: (id: string, isSelected: boolean) => void;
  onSelectAll?: (isSelected: boolean) => void;
}

const RplTable: React.FC<RplTableProps> = ({
  data,
  handleViewMemberDetails,
  handleView,
  handleEdit,
  handleSendEmail,
  handleViewAllEmails,
  handleViewRecordEmails,
  handleDelete,
  handleRestore,
  handleApprove,
  handleChangeStatus,
  handleImpersonateMemberLogin,
  handleAddNote,
  handleViewNoteHistory,
  handleViewInvoiceHistory,
  handleSendProofOfPaymentLink,
  handleCompleteEftPayment,
  handleDownloadForfeitureDocument,
  handleDownloadOriginalCertificate,
  handleMemberClick,
  currentPage,
  totalPages,
  setCurrentPage,
  pageSize,
  setPageSize,
  isLoading,
  sortBy,
  sortDescending,
  onSort,
  selectedIds = new Set(),
  onSelectRow,
  onSelectAll,
}) => {
  const roles = getRoles();
  const isAdmin = roles?.includes('Admin');
  const { getFeedback } = useRplApi();
  const allCurrentPageIds = data.map(row => row.id).filter((id): id is string => !!id);
  const allSelected = allCurrentPageIds.length > 0 && allCurrentPageIds.every(id => selectedIds.has(id));
  const someSelected = allCurrentPageIds.some(id => selectedIds.has(id)) && !allSelected;

  const columns: TableColumn[] = [
    ...(onSelectRow && onSelectAll
      ? [
        {
          label: (
            <input
              type="checkbox"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = someSelected;
              }}
              onChange={(e) => onSelectAll?.(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 dark:border-gray-600"
            />
          ),
          key: 'checkbox',
          render: (row: RplDTO) => (
            <input
              type="checkbox"
              checked={selectedIds.has(row.id ?? '')}
              onChange={(e) => onSelectRow?.(row.id ?? '', e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              className="w-4 h-4 rounded border-gray-300 dark:border-gray-600"
            />
          ),
        },
      ]
      : []),
    {
      label: 'Member',
      key: 'member',
      sortable: true,
      sortKey: 'FirstName',
      showInReview: true,
      render: (row: RplDTO) => (
        <div
          onClick={() => handleMemberClick?.(row.member?.id ?? '')}
          className="flex flex-col gap-1 cursor-pointer"
        >
          <span>
            {row.member?.firstName} {row.member?.lastName}
          </span>
          <span>{row.member?.idPassportNumber ?? ''}</span>
          <MemberNoteIcons member={row.member} tableContext="rpl" recordId={row.id} />
        </div>
      ),
    },
    {
      label: 'Cellphone',
      key: 'cellphone',
      render: (row: RplDTO) => <span>{row.member?.phone}</span>,
    },
    {
      label: 'Email',
      key: 'email',
      render: (row: RplDTO) => <span>{row.member?.email}</span>,
    },
    {
      label: 'Membership',
      key: 'membershipType',
      sortable: true,
      sortKey: 'MemberTypeTitle',
      showInReview: true,
      render: (row: RplDTO) => <span>{row.member?.memberType?.title}</span>,
    },
    {
      label: 'FCA',
      key: 'memberType',
      sortable: true,
      sortKey: 'MemberCategoryTitle',
      showInReview: true,
      render: (row: RplDTO) => <span>{row.member?.memberCategory?.title}</span>,
    },
    {
      label: 'Date Hunt',
      key: 'statusDateHunt',
      sortable: true,
      sortKey: 'DateCertificateHunter',
      showInReview: true,
      render: (row: RplDTO) => {
        return (
          <span>
            {row.member?.dateCertificateHunter
              ? convertToUserTime(row.member?.dateCertificateHunter?.toString() ?? '', true)
              : '-'}
          </span>
        );
      },
    },
    {
      label: 'Date Sport',
      key: 'statusDateSport',
      sortable: true,
      sortKey: 'DateCertificateSport',
      showInReview: true,
      render: (row: RplDTO) => {
        return (
          <span>
            {row.member?.dateCertificateSport
              ? convertToUserTime(row.member?.dateCertificateSport?.toString() ?? '', true)
              : '-'}
          </span>
        );
      },
    },
    {
      label: 'Type',
      key: 'courseType',
      sortable: true,
      sortKey: 'CourseTypeTitle',
      showInReview: true,
      render: (row: RplDTO) => <span>{row.courseType?.title}</span>,
    },

    {
      label: 'Sent',
      key: 'emailStatusSentDate',
      render: (row: RplDTO) => (
        <Mail fill={row.emailStatusSentDate ? 'green' : 'orange'} stroke={'white'} />
      ),
    },

    {
      label: 'Payment',
      key: 'paymentTypeId',
      showInReview: true,
      render: (row: RplDTO) => {
        const base = row.paymentType?.colourGrading?.colour ?? '#ffffffff';
        const { bg, text } = getBadgeColors(base, 0.18, 0.4);
        return base ? (
          <Badge
            label={row.paymentType?.title || 'Unknown'}
            variant="custom"
            customBgColor={bg}
            customTextColor={text}
          />
        ) : (
          <span>{row.paymentType?.title || 'Unknown'}</span>
        );
      },
    },

    {
      label: 'Proof',
      key: 'proofOfPaymentFileId',
      render: (row: RplDTO) => (
        <FileIcons
          files={[
            {
              id: row.proofOfPaymentFileId,
              label: 'Proof of payment',
            },
          ]}
          renderSidePanel={isAdmin ? () => <ReviewPanel row={row} columns={columns} /> : undefined}
        />
      ),
    },


    {
      label: 'Invoiced',
      key: 'invoiceSentDate',
      render: (row: RplDTO) =>
        renderBooleanBadge(row.invoiceCreatedDate ? true : false, 'Yes', 'No'),
    },

    {
      label: 'Status',
      key: 'rplStatusId',
      sortable: true,
      sortKey: 'RplStatusTitle',
      showInReview: true,
      render: (row: RplDTO) => {
        if (row.isDeleted) {
          return <Badge label="Deleted" variant="error" />;
        }
        const base = row.rplStatus?.colourGrading?.colour ?? '#ffffffff';
        const { bg, text } = getBadgeColors(base, 0.18, 0.4);
        return base ? (
          <Badge
            label={row.rplStatus?.title || 'Unknown'}
            variant="custom"
            customBgColor={bg}
            customTextColor={text}
          />
        ) : (
          <span>{row.rplStatus?.title || 'Unknown'}</span>
        );
      },
    },

    {
      label: 'FB',
      key: 'feedback',
      render: (row: RplDTO) => (
        <FeedbackBubble
          hasFeedback={!!row.feedbackFileId}
          fetchFeedback={() => getFeedback(row.id!)}
        />
      ),
    },

    {
      label: 'Updated',
      key: 'updatedUtc',
      sortable: true,
      sortKey: 'UpdatedUtc',
      format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
    },

    {
      label: 'Created',
      key: 'createdUtc',
      sortable: true,
      sortKey: 'CreatedUtc',
      format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
    },
    {
      label: 'Assigned',
      key: 'assigned',
      render: () => <span>None</span>,
    },
  ];

  return (
    <Table
      columns={columns}
      data={data}
      actions={{
        onEdit: handleEdit,
        extra: (row) =>
          createActivityActions({
            onViewMemberDetails: handleViewMemberDetails,
            onView: handleView,
            onApprove: handleApprove,
            onDelete: handleDelete,
            onSendEmail: handleSendEmail,
            onViewAllEmails: handleViewAllEmails,
            onViewRecordEmails: handleViewRecordEmails,
            onRestore: handleRestore,
            onChangeStatus: handleChangeStatus,
            onImpersonateMemberLogin: handleImpersonateMemberLogin,
            onAddNote: handleAddNote,
            onViewNoteHistory: handleViewNoteHistory,
            onViewInvoiceHistory: handleViewInvoiceHistory,
            onSendProofOfPaymentLink: handleSendProofOfPaymentLink,
            onCompleteEftPayment: handleCompleteEftPayment,
            onDownloadForfeitureDocument: handleDownloadForfeitureDocument,
            onDownloadOriginalCertificate: handleDownloadOriginalCertificate,
          })(row),
      }}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={setCurrentPage}
      pageSize={pageSize}
      onPageSizeChange={setPageSize}
      isLoading={isLoading}
      sortBy={sortBy}
      sortDescending={sortDescending}
      onSort={onSort}
    />
  );
};

export default RplTable;
